import {
    egenskapsfilterChanged,
    egenskapsfilterChangedT,
    removeVOTFilter,
    removeVOTFilterT,
} from '@/actions/vegobjekterActions';
import { Egenskapstype, EgenskapTillattVerdi } from '@/domain/vegobjekter/Egenskapstype';
import { Egenskapsfilter, EgenskapsfilterVerdi, Operator, OperatorE } from '@/middleware/queries';
import { RootState, UserState, VegobjekttypeState } from '@/state';
import { hasAccess } from '@/utils/Utils';
import * as React from 'react';
import { connect } from 'react-redux';
import * as selectors from '../../../../selectors/selectors';
import { Loading } from '@/components/objects/Loading';
import { fetchPossibleFilterValues, fetchPossibleFilterValuesT } from '@/middleware/fetchVegobjekter';

interface FilterProps {
    vegobjekttype: VegobjekttypeState;
    filter: Egenskapsfilter;
    egenskapstyper: Egenskapstype[];
    egenskapsfilterChanged: egenskapsfilterChangedT;
    fetchPossibleFilterValues: fetchPossibleFilterValuesT;
    removeVOTFilter: removeVOTFilterT;
    user: UserState;
    possibleFilterValues: string[];
}

interface FilterState {
    validInput: boolean;
    verdi: EgenskapsfilterVerdi | number | string;
    searchFilteredFilterValues: string[];
    dropdownVisible: boolean;
    highlightedIndex: number;
    isLoading: boolean;
}

class EgenskapsfilterContainer extends React.PureComponent<FilterProps, FilterState> {
    private inputRef: React.RefObject<HTMLInputElement> = React.createRef();

    constructor(props: FilterProps) {
        super(props);
        const { verdi } = this.props.filter;
        this.state = {
            validInput: true,
            verdi,
            searchFilteredFilterValues: [],
            dropdownVisible: false,
            highlightedIndex: -1,
            isLoading: false,
        };
    }

    render() {
        return (
            <li className="c-egenskapsfilter">
                <i className="o-icon-filter c-egenskapsfilter__filter-icon" />
                <label className="c-egenskapsfilter__label">Filtrer etter egenskapstype</label>
                <button
                    className="c-egenskapsfilter__remove"
                    onClick={() => this.props.removeVOTFilter(this.props.vegobjekttype, this.props.filter)}
                    aria-label={`Fjern ${this.props.filter.egenskapstype?.navn ?? 'tomt'} filter`}
                >
                    <i className="o-icon-remove c-egenskapsfilter__remove-icon" />
                </button>
                {this.getEgenskapsSelect()}
                <div className="c-egenskapsfilter-operator-verdi-container">
                    {this.getOperatorSelect()}
                    {this.getVerdiInput()}
                </div>
            </li>
        );
    }

    private getEgenskapsSelect = () => {
        const selectedEgenskap = this.props.filter.egenskapstype;
        return (
            <div className="c-egenskapsfilter__select-egenskap-wrapper">
                <select
                    className="c-egenskapsfilter-select c-egenskapsfilter-select__filter"
                    name="egenskap"
                    id="egenskapsfilter_select_filter"
                    value={selectedEgenskap ? selectedEgenskap.id : ''}
                    onChange={e => this.egenskapSelected(e.target.value)}
                    aria-label="Velg egenskapstype å filtrere med"
                >
                    {[
                        <option className="c-egenskapsfilter-select__option" key="null" value="">
                            Velg et filter:
                        </option>,
                    ].concat(
                        this.props.egenskapstyper
                            .filter(e => !e.erStruct())
                            .map(et => (
                                <option
                                    className="c-egenskapsfilter-select__option"
                                    key={et.id}
                                    value={et.id}
                                    disabled={this.isDisabled(et)}
                                >
                                    {et.navn} {this.isDisabled(et) ? '(Ikke tilgjengelig)' : ''}
                                </option>
                            ))
                    )}
                </select>
            </div>
        );
    };

    private isDisabled(egenskapstype: Egenskapstype): boolean {
        return !hasAccess(egenskapstype.sensitivitet, this.props.user.roles);
    }

    private getOperatorSelect() {
        if (this.props.filter.egenskapstype === null) {
            return null;
        }
        const selectedOperator = this.props.filter.operator;
        const selectedEgenskap = this.props.filter.egenskapstype;
        return (
            <div className="c-egenskapsfilter__select-operator-wrapper">
                <i className="o-icon-operators c-egenskapsfilter__operators-icon" />
                <label className="c-egenskapsfilter__label">Operator</label>
                <select
                    className="c-egenskapsfilter-select c-egenskapsfilter-select__operator"
                    disabled={selectedEgenskap === null}
                    name="operator"
                    id="egenskapsfilter_select_operator"
                    onChange={e => this.operatorSelected(e.target.value)}
                    value={selectedOperator || ''}
                    aria-label={'Velg operator'}
                >
                    {[<option className="c-egenskapsfilter-select__option" key="null" value="" />].concat(
                        operatorsForEgenskapstype(selectedEgenskap).map(o => (
                            <option
                                className="c-egenskapsfilter-select__option"
                                key={o}
                                value={o}
                                aria-label={`${
                                    o === OperatorE.NEQ ? 'er ikke lik' : Operator.toStringText(o)
                                }`}
                            >
                                {Operator.toStringText(o)}
                            </option>
                        ))
                    )}
                </select>
            </div>
        );
    }

    private getVerdiInput() {
        const selectedOperator = this.props.filter.operator;
        const selectedEgenskap = this.props.filter.egenskapstype;
        if (
            selectedEgenskap === null ||
            selectedOperator === null ||
            selectedOperator === OperatorE.NO_VALUE ||
            selectedOperator === OperatorE.HAS_VALUE
        ) {
            return null;
        }
        if (selectedEgenskap.erEnum()) {
            const selectedVerdi = (this.props.filter.verdi as EgenskapTillattVerdi) || null;
            return (
                <div className="c-egenskapsfilter__select-verdi-wrapper">
                    <label className="c-egenskapsfilter__label_right">Sett verdi</label>
                    <select
                        className="c-egenskapsfilter-select c-egenskapsfilter-select__value"
                        id="egenskapsfilter_select_option"
                        onChange={e => this.enumverdiChanged(e.target.value)}
                        value={selectedVerdi ? selectedVerdi.id : ''}
                        aria-label="Velg verdi"
                        title={selectedVerdi ? selectedVerdi.verdi : ''}
                    >
                        {[<option className="c-egenskapsfilter-select__option" key="null" value="" />].concat(
                            [...selectedEgenskap.tillatte_verdier]
                                .sort(EgenskapTillattVerdi.comparator)
                                .map(tv => (
                                    <option
                                        className="c-egenskapsfilter-select__option"
                                        key={tv.id}
                                        value={tv.id}
                                    >
                                        {tv.verdi}
                                    </option>
                                ))
                        )}
                    </select>
                </div>
            );
        } else {
            const invalidClassModifier = this.state.validInput
                ? ''
                : 'c-egenskapsfilter-verdi-container__verdi-input--invalid';
            return (
                <div className="c-egenskapsfilter-verdi-container">
                    <label className="c-egenskapsfilter__verdi-container__verdi-label">
                        {this.props.filter.egenskapstype.erNummerisk() ? 'Tall' : 'Sett verdi'}
                    </label>
                    <input
                        type={valueInputType(selectedEgenskap)}
                        className={`c-egenskapsfilter-verdi-container__verdi-input ${invalidClassModifier}`}
                        pattern={selectedEgenskap.erDato() ? '[0-9]{4}-[0-9]{2}-[0-9]{2}' : ''}
                        placeholder={getPlaceholder(selectedEgenskap)}
                        name="verdi"
                        onChange={this.handleChange}
                        onFocus={this.handleFocus}
                        onKeyDown={this.handleKeyDown}
                        onBlur={this.handleBlur}
                        value={(this.state.verdi as number | string) || ''}
                        aria-label="Verdi"
                        id="egenskapsfilter_verdi_container"
                        title={this.state.verdi as string}
                        ref={this.inputRef}
                    />
                    {this.props.filter.egenskapstype?.erTekst() && (
                        <div className="c-egenskapsfilter__info-icon-container">
                            <i className="o-icon-info c-egenskapsfilter__info-icon" />
                            <div className="c-egenskapsfilter__info-box">
                                <p>
                                    Du kan bruke <strong>*</strong> for å gjøre vilkårlige søk:
                                </p>
                                <ul>
                                    <li>
                                        <strong>*ABC*</strong> vil filtrere på alt som inneholder{' '}
                                        <strong>ABC</strong>.
                                    </li>
                                    <li>
                                        <strong>4*meter</strong> vil filtrere på alt som slutter på{' '}
                                        <strong>meter</strong> og starter på <strong>4</strong>.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )}
                    {!this.state.validInput && <p>Ugyldig inputverdi</p>}
                    {this.state.dropdownVisible && this.renderDropdown()}
                </div>
            );
        }
    }

    private handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const validInput = getValidationRegex(this.props.filter.egenskapstype).test(e.target.value);
        this.setState({ validInput, verdi: e.target.value });
        this.filterpossibleFilterValues(e.target.value);
    };

    private handleFocus = () => {
        this.setState({
            dropdownVisible: true,
            isLoading: true,
        });
        this.props.fetchPossibleFilterValues(this.props.vegobjekttype, this.props.filter);
    };

    private handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
            this.setState({
                dropdownVisible: false,
                highlightedIndex: -1,
            });
        }
    };

    private egenskapSelected(value: string) {
        const egenskapsId = parseInt(value, 10);
        const selectedEgenskap = this.props.egenskapstyper.find(et => et.id === egenskapsId) || null;
        const newFilter = this.props.filter.withEgenskapstypeReset(selectedEgenskap);
        this.props.egenskapsfilterChanged(this.props.vegobjekttype, newFilter);
    }

    private operatorSelected(value: string) {
        const operator = parseInt(value, 10) as OperatorE;
        const newFilter = this.props.filter.withOperator(isNaN(operator) ? null : operator);
        this.props.egenskapsfilterChanged(this.props.vegobjekttype, newFilter);
    }

    private enumverdiChanged(enumIdString: string) {
        const enumId = parseInt(enumIdString, 10);
        const verdi = this.props.filter.egenskapstype.tillatte_verdier.find(tv => tv.id === enumId) || null;
        const newFilter = this.props.filter.withVerdi(verdi);
        this.props.egenskapsfilterChanged(this.props.vegobjekttype, newFilter);
    }

    private handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const { highlightedIndex, searchFilteredFilterValues } = this.state;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                this.setState(
                    prevState => ({
                        highlightedIndex:
                            (prevState.highlightedIndex + 1) % searchFilteredFilterValues.length,
                    }),
                    () => this.scrollIntoView()
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                this.setState(
                    prevState => ({
                        highlightedIndex:
                            (prevState.highlightedIndex - 1 + searchFilteredFilterValues.length) %
                            searchFilteredFilterValues.length,
                    }),
                    () => this.scrollIntoView()
                );
                break;
            case 'Enter':
                if (highlightedIndex >= 0 && highlightedIndex < searchFilteredFilterValues.length) {
                    this.selectValue(searchFilteredFilterValues[highlightedIndex]);
                    this.setState({ validInput: true });
                } else if (this.state.validInput) {
                    const rawValue = (this.state.verdi || '').toString().replace(/['"]/g, '').trim();
                    const value = this.getValueFromString(rawValue);
                    if (value !== null && value !== '') {
                        const newFilter = this.props.filter.withVerdi(value);
                        this.props.egenskapsfilterChanged(this.props.vegobjekttype, newFilter);
                        this.inputRef.current?.blur();
                    }
                }
                break;
            case 'Escape':
                this.inputRef.current?.blur();
                break;
            default:
                break;
        }
    };

    private scrollIntoView() {
        const dropdown = document.querySelector('.c-egenskapsfilter-dropdown');
        const highlightedItem = dropdown?.querySelector('.highlighted');
        if (highlightedItem) {
            highlightedItem.scrollIntoView({ block: 'nearest' });
        }
    }

    private getValueFromString(rawValue: string) {
        if (this.props.filter.egenskapstype.erNummerisk()) {
            rawValue = rawValue.replace(',', '.');
            const value: number = rawValue.includes('.') ? parseFloat(rawValue) : parseInt(rawValue, 10);
            return isNaN(value) ? null : value;
        }
        return rawValue;
    }

    private filterpossibleFilterValues(inputValue: string) {
        const erNummerisk = this.props.filter.egenskapstype.erNummerisk();
        const filteredValues = this.props.vegobjekttype.possibleFilterValues.filter(value =>
            erNummerisk
                ? value.startsWith(inputValue)
                : value.toLowerCase().includes(inputValue.toLowerCase())
        );
        this.setState({ searchFilteredFilterValues: filteredValues });
    }

    componentDidUpdate(prevProps: FilterProps) {
        if (prevProps.vegobjekttype.possibleFilterValues !== this.props.vegobjekttype.possibleFilterValues) {
            this.setState({
                searchFilteredFilterValues: this.props.vegobjekttype.possibleFilterValues,
                isLoading: false,
            });
        }
    }

    private renderDropdown() {
        const { highlightedIndex, searchFilteredFilterValues, isLoading } = this.state;
        return (
            <ul className="c-egenskapsfilter-dropdown" onMouseDown={this.handleMouseDown}>
                {isLoading ? (
                    <li className="c-egenskapsfilter-dropdown__item">
                        <Loading type="text" className="loading-spinner" />
                    </li>
                ) : searchFilteredFilterValues.length === 0 ? (
                    <li className="c-egenskapsfilter-dropdown__item">Finner ingen gyldige filter</li>
                ) : (
                    searchFilteredFilterValues.map((value, index) => (
                        <li
                            key={index}
                            className={`c-egenskapsfilter-dropdown__item ${
                                index === highlightedIndex ? 'highlighted' : ''
                            }`}
                            onClick={() => {
                                this.selectValue(value);
                                this.setState({ validInput: true });
                            }}
                        >
                            {value}
                        </li>
                    ))
                )}
            </ul>
        );
    }

    private handleMouseDown = (e: React.MouseEvent<HTMLUListElement>) => {
        e.preventDefault();
    };

    private selectValue(value: string) {
        this.setState({
            verdi: value,
            dropdownVisible: false,
        });
        const newFilter = this.props.filter.withVerdi(value);
        this.props.egenskapsfilterChanged(this.props.vegobjekttype, newFilter);
        this.inputRef.current?.blur();
    }
}

const mapDispatchToProps = {
    egenskapsfilterChanged,
    removeVOTFilter,
    fetchPossibleFilterValues,
};
const mapStateToProps = (state: RootState) => ({
    user: selectors.stateUser(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(EgenskapsfilterContainer);

function getPlaceholder(egenskapstype: Egenskapstype): string {
    switch (true) {
        case egenskapstype.erHeltall():
            return 'eks: 1000';
        case egenskapstype.erFlyttall():
            return 'eks: 1000.0';
        case egenskapstype.erTekst():
            return '(Tekst)';
        case egenskapstype.erDato():
            return 'yyyy-mm-dd';
        case egenskapstype.erKlokkeslett():
            return 'hhmm';
        default:
            return 'Verdi';
    }
}

function getValidationRegex(egenskapstype: Egenskapstype): RegExp {
    switch (true) {
        case egenskapstype.erFlyttall():
            return /^-?\d+[,.]?\d*$/;
        case egenskapstype.erHeltall():
            return /^-?\d+$/;
        case egenskapstype.erDato():
            // This will match yyyy-mm-dd and also yyyy-m-d
            return /^\d{4}-(0?[1-9]|1[012])-(0?[1-9]|[12]\d|3[01])$/;
        default:
            return /.+$/;
    }
}

function operatorsForEgenskapstype(egenskapstype: Egenskapstype): OperatorE[] {
    if (egenskapstype === null) return [];
    const operatorer = [];
    if (!egenskapstype.erGeometri()) {
        operatorer.push(OperatorE.EQ, OperatorE.NEQ);
    }
    if (egenskapstype.erNummerisk() || egenskapstype.erDato() || egenskapstype.erKlokkeslett()) {
        operatorer.push(OperatorE.BEQ, OperatorE.LEQ, OperatorE.LT, OperatorE.GT);
    }
    operatorer.push(OperatorE.HAS_VALUE, OperatorE.NO_VALUE);
    return operatorer;
}

function valueInputType(egenskapstype: Egenskapstype): string {
    if (egenskapstype.erNummerisk()) {
        return 'number';
    } else if (egenskapstype.erDato()) {
        return 'date';
    }
    return 'text';
}
